import React, { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { getAuthorizedUserRole } from '../utils/authConfig';
import { getProductVariants } from '../utils/productVariants';
import {
  createProductSamples, MAX_SAMPLE_BATCH, MAX_SAMPLE_NUMBER_LENGTH,
  sampleErrorMessage, subscribeProductSamples, updateProductSample,
} from '../utils/productSamples';

export const sampleStyles = {
  card: { border: '1px solid #dbe3ed', borderRadius: 12, padding: 16, marginTop: 16, background: '#fff', color: '#1e293b' },
  row: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' },
  label: { display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 150px', fontSize: 14 },
  input: { padding: '10px 12px', border: '1px solid #bac6d3', borderRadius: 6, width: '100%', boxSizing: 'border-box', minHeight: 42 },
  button: { padding: '10px 14px', border: '1px solid #94a3b8', borderRadius: 6, background: '#f1f5f9', color: '#0f172a', cursor: 'pointer', minHeight: 42 },
  primary: { padding: '10px 14px', border: 0, borderRadius: 6, background: '#1d4ed8', color: '#fff', cursor: 'pointer', minHeight: 42 },
  error: { color: '#991b1b', background: '#fef2f2', padding: 10, borderRadius: 6 },
  muted: { color: '#526174', fontSize: 13 },
};

export function useSampleAuthorization() {
  const [state, setState] = useState({ loading: true, authorized: false });
  useEffect(() => {
    let active = true;
    let request = 0;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const currentRequest = ++request;
      setState({ loading: true, authorized: false });
      try {
        const role = await getAuthorizedUserRole(user);
        if (active && currentRequest === request) setState({ loading: false, authorized: ['admin', 'staff'].includes(role) });
      } catch (error) {
        if (active && currentRequest === request) setState({ loading: false, authorized: false });
      }
    });
    return () => { active = false; unsubscribe(); };
  }, []);
  return state;
}

// Each row owns its unsaved input. Live snapshots never overwrite a dirty field.
export function SampleEditorRow({ sample, label, selected, onSelect, onSaved, onDirtyChange, inputRef, disabled = false }) {
  const [baseline, setBaseline] = useState(sample);
  const [number, setNumber] = useState(sample.number || '');
  const [location, setLocation] = useState(sample.location || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const dirty = number !== (baseline.number || '') || location !== (baseline.location || '');
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(sample.id, dirty);
  }, [sample.id, dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty && sample.revision > baseline.revision) {
      setBaseline(sample);
      setNumber(sample.number || '');
      setLocation(sample.location || '');
    }
  }, [sample, dirty, baseline.revision]);
  const save = async () => {
    if (busy || disabled) return;
    if (!dirty) { if (onSaved) onSaved(sample.id); return; }
    setBusy(true);
    setError(null);
    setSaved(false);
    const changes = {};
    if (number !== (baseline.number || '')) changes.number = number;
    if (location !== (baseline.location || '')) changes.location = location;
    try {
      const updated = await updateProductSample({
        sampleId: sample.id, country: sample.country, productId: sample.productId, expected: baseline, changes,
      });
      setBaseline(updated);
      setNumber(updated.number || '');
      setLocation(updated.location || '');
      setSaved(true);
      if (onSaved) onSaved(sample.id);
    } catch (failure) {
      setError(failure);
    } finally { setBusy(false); }
  };
  const onEnter = (event) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.stopPropagation();
      save();
    }
  };
  const resolveConflict = (keepInput) => {
    const current = error?.current || sample;
    const editedNumber = number !== (baseline.number || '');
    const editedLocation = location !== (baseline.location || '');
    setBaseline(current);
    if (!keepInput || !editedNumber) setNumber(current.number || '');
    if (!keepInput || !editedLocation) setLocation(current.location || '');
    setError(null);
    setSaved(false);
  };
  return (
    <div style={{ borderTop: '1px solid #e2e8f0', padding: '14px 0' }}>
      <div style={{ ...sampleStyles.row, marginBottom: 8, alignItems: 'center' }}>
        {onSelect && <input type="checkbox" checked={Boolean(selected)} onChange={() => onSelect(sample.id)} aria-label={`選取 ${label || sample.id}`} disabled={busy || disabled || dirty || sample.number !== ''} />}
        <strong>{label || '實體樣品'}</strong>
        <small style={sampleStyles.muted}>紀錄 {sample.id.slice(-6)} · 版本 {sample.revision}</small>
      </div>
      <div style={sampleStyles.row}>
        <label style={sampleStyles.label}>樣品編號（可留空）
          <input ref={inputRef} type="text" value={number} maxLength={MAX_SAMPLE_NUMBER_LENGTH} autoComplete="off" style={sampleStyles.input}
            onChange={(event) => { setNumber(event.target.value); setSaved(false); }} onKeyDown={onEnter} disabled={busy || disabled} />
        </label>
        <label style={sampleStyles.label}>位置
          <input type="text" value={location} maxLength={200} style={sampleStyles.input}
            onChange={(event) => { setLocation(event.target.value); setSaved(false); }} onKeyDown={onEnter} disabled={busy || disabled} placeholder="例如：A架／直播桌" />
        </label>
        <button type="button" onClick={save} disabled={busy || disabled || !dirty} style={sampleStyles.primary}>{busy ? '儲存中…' : '儲存此件'}</button>
      </div>
      {saved && <p role="status" style={sampleStyles.muted}>已儲存</p>}
      {dirty && sample.revision !== baseline.revision && !error && <p style={sampleStyles.muted}>這件樣品有較新資料；你的輸入已保留。儲存時會檢查衝突</p>}
      {error && <div role="alert" style={{ ...sampleStyles.error, marginTop: 10 }}>
        <div>{sampleErrorMessage(error)}</div>
        {error.code === 'sample-conflict' && <>
          <p>最新編號：{(error.current || sample).number || '未編號'}；最新位置：{(error.current || sample).location || '未設定'}</p>
          <div style={sampleStyles.row}>
            <button type="button" style={sampleStyles.button} onClick={() => resolveConflict(false)}>採用最新資料</button>
            <button type="button" style={sampleStyles.button} onClick={() => resolveConflict(true)}>保留我的輸入，重新確認後儲存</button>
          </div>
        </>}
      </div>}
    </div>
  );
}

export default function ProductSamples({ country, productId, product, onChange }) {
  const authorization = useSampleAuthorization();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [variantId, setVariantId] = useState('');
  const [count, setCount] = useState('1');
  const [number, setNumber] = useState('');
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const variants = getProductVariants(product || {});
  const selectedVariant = variants.some((variant) => variant.id === variantId && variant.active !== false) ? variantId : '';
  useEffect(() => {
    setLoading(true);
    setSamples([]);
    setError('');
    if (!authorization.authorized || !productId) return undefined;
    return subscribeProductSamples(country, productId, (next) => {
      setSamples(next);
      setLoading(false);
      if (onChangeRef.current) onChangeRef.current(next);
    }, (failure) => { setError(sampleErrorMessage(failure)); setLoading(false); });
  }, [country, productId, authorization.authorized]);
  const add = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (!/^\d+$/.test(count)) throw new Error('件數須為整數');
      const ids = await createProductSamples({ country, productId, variantId: selectedVariant, count: Number(count), number, location });
      setNumber('');
      setMessage(`已新增 ${ids.length} 件實體樣品`);
    } catch (failure) { setError(sampleErrorMessage(failure)); }
    finally { setBusy(false); }
  };
  return (
    <section style={sampleStyles.card} aria-label="實體樣品管理" onKeyDown={(event) => {
      // This panel can live inside EditProduct's form; Enter must not submit that form.
      if (event.key === 'Enter' && event.target.tagName === 'INPUT') event.preventDefault();
    }}>
      <h3 style={{ marginTop: 0 }}>實體樣品</h3>
      <p style={sampleStyles.muted}>同款商品可以有多件樣品，每件各有編號與位置。編號全國家共用且不可重複，保留前導零與大小寫。商品條碼和庫存數量不受影響</p>
      {authorization.loading ? <p role="status">確認登入身份…</p> : !authorization.authorized ? <p role="alert">請先使用管理員或工作人員帳號登入</p> : <>
        <div style={sampleStyles.row}>
          <label style={sampleStyles.label}>款式
            <select value={selectedVariant} onChange={(event) => setVariantId(event.target.value)} disabled={busy} style={sampleStyles.input}>
              <option value="">商品本體／未指定款式</option>
              {variants.filter((variant) => variant.active !== false).map((variant) => <option value={variant.id} key={variant.id}>{variant.name || '無款式'} · {variant.rawBarcode || variant.barcode || '無條碼'}</option>)}
            </select>
          </label>
          <label style={{ ...sampleStyles.label, flex: '0 1 90px' }}>件數
            <input type="number" value={count} min="1" max={MAX_SAMPLE_BATCH} step="1" onChange={(event) => setCount(event.target.value)} disabled={busy} style={sampleStyles.input} />
          </label>
          <label style={sampleStyles.label}>樣品編號（多件須留空）
            <input type="text" value={number} onChange={(event) => setNumber(event.target.value)} maxLength={MAX_SAMPLE_NUMBER_LENGTH} disabled={busy} style={sampleStyles.input} autoComplete="off" />
          </label>
          <label style={sampleStyles.label}>位置
            <input type="text" value={location} onChange={(event) => setLocation(event.target.value)} maxLength={200} disabled={busy} style={sampleStyles.input} />
          </label>
          <button type="button" onClick={add} disabled={busy || !productId} style={sampleStyles.primary}>{busy ? '新增中…' : '新增實體樣品'}</button>
        </div>
        <p style={sampleStyles.muted}>新增款式請先儲存商品，再新增實體樣品。新增多件未編號樣品後，可到「樣品補編」一次分配編號</p>
        {error && <p role="alert" style={sampleStyles.error}>{error}</p>}
        {message && <p role="status">{message}</p>}
        {loading ? <p role="status">讀取樣品…</p> : samples.length === 0 ? <p style={sampleStyles.muted}>此商品尚無實體樣品紀錄</p> : <>
          <p style={sampleStyles.muted}>共 {samples.length} 件，未編號 {samples.filter((sample) => !sample.number).length} 件</p>
          {samples.map((sample) => <SampleEditorRow key={sample.id} sample={sample}
            label={sample.variantId ? variants.find((variant) => variant.id === sample.variantId)?.name || `原款式 ${sample.variantId}` : '商品本體／未指定款式'} />)}
        </>}
      </>}
    </section>
  );
}
